import { verifiedUsefulInfo, VERIFIED_USEFUL_INFO_VERSION } from "./usefulInfoVerifiedFacts";
import type { SupportedLanguage } from "../types/domain";
import type { UsefulInfoFieldId } from "./tripStore";
import { getUsefulInfoGlobalSnapshot, USEFUL_INFO_GLOBAL_SNAPSHOT_META } from "../data/usefulInfoGlobalSnapshot";

const RATE_CACHE_PREFIX = "mundely.usefulInfo.rate.v1";
const EMERGENCY_CACHE_PREFIX = "mundely.usefulInfo.emergency.v1";
const REQUIREMENTS_INDEX_CACHE_KEY = "mundely.usefulInfo.requirements.index.v1";
const REQUIREMENTS_DESTINATION_CACHE_PREFIX = "mundely.usefulInfo.requirements.destination.v1";
const COUNTRY_INTELLIGENCE_CACHE_PREFIX = "mundely.usefulInfo.countryIntelligence.v1";
const RATE_CACHE_MS = 6 * 60 * 60 * 1000;
const EMERGENCY_CACHE_MS = 7 * 24 * 60 * 60 * 1000;
const REQUIREMENTS_CACHE_MS = 24 * 60 * 60 * 1000;
const COUNTRY_INTELLIGENCE_CACHE_MS = 30 * 24 * 60 * 60 * 1000;
const REQUIREMENTS_INDEX_URL = "https://travelrequirements.info/data/index.json";

interface FrankfurterResponse {
  date?: string;
  rates?: Record<string, number>;
}

interface EmergencySection {
  All?: Array<string | null>;
  GSM?: Array<string | null>;
  Fixed?: Array<string | null>;
}

interface EmergencyCountryData {
  Dispatch?: EmergencySection;
  Police?: EmergencySection;
  Ambulance?: EmergencySection;
  Fire?: EmergencySection;
  Member_112?: boolean;
  LocalOnly?: boolean;
}

interface EmergencyApiResponse {
  error?: string | null;
  data?: EmergencyCountryData;
}

export interface EmergencyLabels {
  dispatch: string;
  police: string;
  ambulance: string;
  fire: string;
}

interface CachedValue<T> {
  at: number;
  value: T;
}

interface TravelRequirementsIndexDestination {
  id?: string;
  iso2?: string;
  name?: string;
  url?: string;
  lastUpdated?: string;
  oldestVerified?: string;
}

interface TravelRequirementsIndex {
  version?: string;
  dateModified?: string;
  destinations?: TravelRequirementsIndexDestination[];
}

interface TravelVisaRule {
  requirement?: string;
  maxStayDays?: number | null;
  extendableDays?: number | null;
  notes?: string;
}

interface TravelPassportValidity {
  rule?: string;
  text?: string;
}

interface TravelVaccinationRequirement {
  id?: string;
  name?: string;
  certificate?: string;
  requiredWhen?: string;
  text?: string;
}

interface TravelDrivingIdp {
  required?: boolean | null;
  conventions?: string[];
  text?: string;
}

interface TravelCountryFacts {
  emergency?: {
    dispatch?: string;
    police?: string;
    ambulance?: string;
    fire?: string;
  };
  driving?: {
    idp?: TravelDrivingIdp;
  };
  tapWaterSafe?: boolean | null;
  safety?: { level?: string };
}

interface TravelDestinationRecord {
  iso2?: string;
  meta?: {
    lastUpdated?: string;
  };
  visaPolicy?: {
    byPassport?: Record<string, TravelVisaRule>;
  };
  entryRequirements?: {
    passportValidity?: TravelPassportValidity;
    blankPages?: {
      count?: number | null;
    };
    vaccinations?: TravelVaccinationRequirement[] | TravelVaccinationRequirement | null;
  };
  countryFacts?: TravelCountryFacts;
}

interface WikidataBindingValue { value?: string }
interface WikidataCountryIntelligenceResponse {
  results?: { bindings?: Array<{ governmentLabel?: WikidataBindingValue; religionLabel?: WikidataBindingValue }> };
}
interface CountryIntelligence { governmentType?: string; religion?: string }

interface WikidataSearchItem {
  id?: string;
  label?: string;
  description?: string;
}

interface WikidataSearchResponse {
  search?: WikidataSearchItem[];
}

interface DiplomaticMissionResult {
  embassy?: string;
  consulates?: string;
}

export interface UsefulInfoTravelRequirementsResult {
  values: Partial<Record<UsefulInfoFieldId, string>>;
  datasetVersion: string;
  updatedAt: string;
}

const FALLBACK_EMERGENCY_NUMBERS: Partial<Record<string, (labels: EmergencyLabels) => string>> = {
  JP: (labels) => `${labels.police}: 110 · ${labels.ambulance}/${labels.fire}: 119`,
  DE: (labels) => `${labels.dispatch}: 112 · ${labels.police}: 110`,
  IT: (labels) => `${labels.dispatch}: 112`
};

function fallbackEmergencyNumbers(countryCode: string, labels: EmergencyLabels): string {
  return FALLBACK_EMERGENCY_NUMBERS[countryCode]?.(labels) || "";
}

function localeForLanguage(language: SupportedLanguage): string {
  if (language === "it") return "it-IT";
  if (language === "es") return "es-ES";
  return "en-GB";
}

function localized(language: SupportedLanguage, it: string, en: string, es: string): string {
  if (language === "it") return it;
  if (language === "es") return es;
  return en;
}

function readCache<T>(key: string, maxAgeMs: number): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedValue<T>;
    if (!parsed || typeof parsed.at !== "number" || Date.now() - parsed.at > maxAgeMs) return null;
    return parsed.value;
  } catch {
    return null;
  }
}

function writeCache<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify({ at: Date.now(), value } satisfies CachedValue<T>));
  } catch {
    // Cache is optional.
  }
}

async function fetchJson<T>(url: string): Promise<T | null> {
  try {
    const response = await fetch(url, { headers: { Accept: "application/json" }, cache: "no-store" });
    if (!response.ok) return null;
    return await response.json() as T;
  } catch {
    return null;
  }
}

function validCurrency(value: string): string {
  const code = String(value || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : "";
}

export async function fetchUsefulInfoExchangeRate(
  referenceCurrency: string,
  destinationCurrency: string,
  language: SupportedLanguage
): Promise<{ value: string; sourceDate: string } | null> {
  const base = validCurrency(referenceCurrency);
  const quote = validCurrency(destinationCurrency);
  if (!base || !quote) return null;

  const formatter = new Intl.NumberFormat(localeForLanguage(language), { maximumFractionDigits: 6 });
  if (base === quote) return { value: `1 ${base} = 1 ${quote}`, sourceDate: new Date().toISOString().slice(0, 10) };

  const cacheKey = `${RATE_CACHE_PREFIX}.${base}.${quote}`;
  const cached = readCache<{ rate: number; date: string }>(cacheKey, RATE_CACHE_MS);
  if (cached && Number.isFinite(cached.rate) && cached.rate > 0) {
    return { value: `1 ${base} = ${formatter.format(cached.rate)} ${quote}`, sourceDate: cached.date };
  }

  try {
    const response = await fetch(
      `https://api.exchangerate-api.com/v4/latest/${encodeURIComponent(base)}`,
      { headers: { Accept: "application/json" }, cache: "no-store" }
    );
    if (!response.ok) return null;
    const data = await response.json() as FrankfurterResponse;
    const rate = Number(data.rates?.[quote]);
    if (!Number.isFinite(rate) || rate <= 0) return null;
    const date = String(data.date || new Date().toISOString().slice(0, 10));
    writeCache(cacheKey, { rate, date });
    return { value: `1 ${base} = ${formatter.format(rate)} ${quote}`, sourceDate: date };
  } catch {
    return null;
  }
}

function collectNumbers(section: EmergencySection | undefined): string[] {
  if (!section) return [];
  const values = [...(section.All || []), ...(section.GSM || []), ...(section.Fixed || [])]
    .map((value) => String(value || "").trim())
    .filter((value) => /^\+?[0-9][0-9\s-]*$/.test(value));
  return [...new Set(values)];
}

function summarizeEmergency(data: EmergencyCountryData, labels: EmergencyLabels): string {
  const dispatch = collectNumbers(data.Dispatch);
  const police = collectNumbers(data.Police);
  const ambulance = collectNumbers(data.Ambulance);
  const fire = collectNumbers(data.Fire);
  if (data.Member_112 && !dispatch.includes("112")) dispatch.unshift("112");

  const parts: string[] = [];
  if (dispatch.length) parts.push(`${labels.dispatch}: ${dispatch.join(" / ")}`);
  if (police.length && police.some((number) => !dispatch.includes(number))) parts.push(`${labels.police}: ${police.join(" / ")}`);
  if (ambulance.length && ambulance.some((number) => !dispatch.includes(number))) parts.push(`${labels.ambulance}: ${ambulance.join(" / ")}`);
  if (fire.length && fire.some((number) => !dispatch.includes(number)) && fire.join("/") !== ambulance.join("/")) parts.push(`${labels.fire}: ${fire.join(" / ")}`);
  return parts.join(" · ");
}

export async function fetchUsefulInfoEmergencyNumbers(
  countryCode: string,
  labels: EmergencyLabels
): Promise<string> {
  const code = String(countryCode || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "";
  const cacheKey = `${EMERGENCY_CACHE_PREFIX}.${code}`;
  const cached = readCache<EmergencyCountryData>(cacheKey, EMERGENCY_CACHE_MS);
  if (cached) return summarizeEmergency(cached, labels);

  const fallback = fallbackEmergencyNumbers(code, labels);

  try {
    const response = await fetch(`https://emergencynumberapi.com/api/country/${encodeURIComponent(code)}`, {
      headers: { Accept: "application/json" },
      cache: "no-store"
    });
    if (!response.ok) return fallback;
    const result = await response.json() as EmergencyApiResponse;
    if (result.error || !result.data) return fallback;
    writeCache(cacheKey, result.data);
    return summarizeEmergency(result.data, labels);
  } catch {
    return fallback;
  }
}

function formatVisaRule(rule: TravelVisaRule | undefined, language: SupportedLanguage): string {
  if (!rule) return "";
  const stay = typeof rule.maxStayDays === "number" && rule.maxStayDays > 0
    ? localized(language, ` fino a ${rule.maxStayDays} giorni`, ` for up to ${rule.maxStayDays} days`, ` hasta ${rule.maxStayDays} días`)
    : "";

  switch (rule.requirement) {
    case "freedom-of-movement":
      return localized(
        language,
        "Nessun visto richiesto per la tua nazionalità: si applica la libertà di movimento. Porta un documento di viaggio valido.",
        "No visa is required for your nationality: freedom of movement applies. Carry a valid travel document.",
        "No se requiere visado para tu nacionalidad: se aplica la libertad de circulación. Lleva un documento de viaje válido."
      );
    case "visa-free":
      return localized(language, `Visto non richiesto per soggiorni turistici${stay}.`, `Visa not required for tourist stays${stay}.`, `No se requiere visado para estancias turísticas${stay}.`);
    case "eta":
      return localized(language, `Autorizzazione elettronica di viaggio (ETA) richiesta prima della partenza${stay}.`, `Electronic travel authorisation (ETA) required before departure${stay}.`, `Se requiere una autorización electrónica de viaje (ETA) antes de la salida${stay}.`);
    case "evisa":
      return localized(language, `Visto elettronico richiesto prima della partenza${stay}.`, `Electronic visa required before departure${stay}.`, `Se requiere visado electrónico antes de la salida${stay}.`);
    case "visa-on-arrival":
      return localized(language, `Visto all'arrivo previsto per questa nazionalità${stay}. Verifica condizioni e documenti prima della partenza.`, `Visa on arrival is available for this nationality${stay}. Check conditions and documents before departure.`, `Hay visado a la llegada para esta nacionalidad${stay}. Verifica condiciones y documentos antes de salir.`);
    case "embassy-visa":
      return localized(language, "Visto richiesto prima della partenza tramite ambasciata o consolato.", "Visa required before departure through an embassy or consulate.", "Se requiere visado antes de la salida a través de una embajada o consulado.");
    case "banned":
      return localized(language, "Il dataset segnala l'ingresso come non consentito per questa nazionalità. Verifica immediatamente con le autorità competenti.", "The dataset marks entry as not permitted for this nationality. Verify immediately with the competent authorities.", "El conjunto de datos marca la entrada como no permitida para esta nacionalidad. Verifica inmediatamente con las autoridades competentes.");
    case "unconfirmed":
      return localized(language, "Requisito visto non confermato per questa nazionalità. Verifica con le autorità consolari prima della partenza.", "Visa requirement is not confirmed for this nationality. Check with the consular authorities before departure.", "El requisito de visado no está confirmado para esta nacionalidad. Consulta a las autoridades consulares antes de viajar.");
    default:
      return "";
  }
}

function formatPassportRequirement(
  passport: TravelPassportValidity | undefined,
  blankPages: number | null | undefined,
  visaRule: TravelVisaRule | undefined,
  language: SupportedLanguage
): string {
  if (visaRule?.requirement === "freedom-of-movement") {
    return localized(
      language,
      "Per la tua nazionalità è indicata la libertà di movimento: usa un documento di viaggio valido (passaporto o carta d'identità quando ammessa).",
      "Freedom of movement applies to your nationality: use a valid travel document (passport or national ID card where accepted).",
      "Para tu nacionalidad se aplica la libertad de circulación: utiliza un documento de viaje válido (pasaporte o documento nacional de identidad cuando se admita)."
    );
  }

  let value = "";
  switch (passport?.rule) {
    case "valid-for-stay":
      value = localized(language, "Passaporto valido per tutta la durata del soggiorno.", "Passport valid for the full duration of the stay.", "Pasaporte válido durante toda la estancia.");
      break;
    case "3-months-beyond-departure":
      value = localized(language, "Passaporto valido per almeno 3 mesi oltre la partenza prevista dal Paese/area.", "Passport valid for at least 3 months beyond the planned departure from the country/area.", "Pasaporte válido al menos 3 meses después de la salida prevista del país/zona.");
      break;
    case "6-months-beyond-stay":
      value = localized(language, "Passaporto valido per almeno 6 mesi oltre il soggiorno previsto.", "Passport valid for at least 6 months beyond the planned stay.", "Pasaporte válido al menos 6 meses después de la estancia prevista.");
      break;
    case "6-months-on-arrival":
      value = localized(language, "Passaporto con almeno 6 mesi di validità al momento dell'arrivo.", "Passport with at least 6 months' validity on arrival.", "Pasaporte con al menos 6 meses de validez a la llegada.");
      break;
    case "other":
      value = localized(language, "Sono previsti requisiti specifici di validità del passaporto: verifica la fonte ufficiale prima della partenza.", "Specific passport-validity rules apply: check the official source before departure.", "Se aplican requisitos específicos de validez del pasaporte: verifica la fuente oficial antes de viajar.");
      break;
    default:
      return "";
  }

  if (typeof blankPages === "number" && blankPages > 0) {
    value += localized(language, ` Pagine libere indicate: ${blankPages}.`, ` Blank pages indicated: ${blankPages}.`, ` Páginas en blanco indicadas: ${blankPages}.`);
  }
  return value;
}

function formatVaccinationRequirements(
  vaccinations: TravelVaccinationRequirement[] | TravelVaccinationRequirement | null | undefined,
  language: SupportedLanguage
): string {
  const items = Array.isArray(vaccinations) ? vaccinations : vaccinations ? [vaccinations] : [];
  const mandatory = items.filter((item) => item.requiredWhen === "always" || item.requiredWhen === "arriving-from-risk-country");
  if (!mandatory.length) {
    return localized(
      language,
      "Nessun vaccino obbligatorio indicato nei requisiti d'ingresso consultati. Verifica comunque le indicazioni sanitarie ufficiali prima della partenza.",
      "No mandatory vaccine is listed in the entry requirements consulted. Still check official health guidance before departure.",
      "No figura ninguna vacuna obligatoria en los requisitos de entrada consultados. Verifica igualmente las indicaciones sanitarias oficiales antes de viajar."
    );
  }

  return mandatory.map((item) => {
    const name = String(item.name || item.id || "").trim();
    if (item.requiredWhen === "always") {
      return localized(language, `Obbligatorio: ${name}.`, `Required: ${name}.`, `Obligatoria: ${name}.`);
    }
    return localized(
      language,
      `Richiesto in base al Paese di provenienza/transito: ${name}.`,
      `Required depending on the country of origin/transit: ${name}.`,
      `Requerida según el país de procedencia/tránsito: ${name}.`
    );
  }).join(" ");
}

function conventionLabel(value: string, language: SupportedLanguage): string {
  if (value === "1949-geneva") return localized(language, "Convenzione di Ginevra 1949", "1949 Geneva Convention", "Convenio de Ginebra de 1949");
  if (value === "1968-vienna") return localized(language, "Convenzione di Vienna 1968", "1968 Vienna Convention", "Convenio de Viena de 1968");
  return value;
}

function formatDrivingPermit(idp: TravelDrivingIdp | undefined, language: SupportedLanguage): string {
  if (!idp) return "";
  const conventions = (idp.conventions || []).map((item) => conventionLabel(item, language)).filter(Boolean);
  const suffix = conventions.length
    ? localized(language, ` Convenzione rilevante: ${conventions.join(" / ")}.`, ` Relevant convention: ${conventions.join(" / ")}.`, ` Convenio relevante: ${conventions.join(" / ")}.`)
    : "";

  if (idp.required === true) {
    return localized(language, "Patente internazionale richiesta.", "International driving permit required.", "Se requiere permiso internacional de conducción.") + suffix;
  }
  if (idp.required === false) {
    return localized(language, "Patente internazionale non indicata come obbligatoria.", "International driving permit is not indicated as mandatory.", "El permiso internacional de conducción no figura como obligatorio.") + suffix;
  }
  if (conventions.length) {
    return localized(
      language,
      "Requisito da verificare per la tua patente nazionale.",
      "Requirement must be checked for your national driving licence.",
      "El requisito debe verificarse para tu permiso de conducción nacional."
    ) + suffix;
  }
  return "";
}

function formatTravelRequirementsEmergency(facts: TravelCountryFacts | undefined, language: SupportedLanguage): string {
  const emergency = facts?.emergency;
  if (!emergency) return "";
  const parts: string[] = [];
  if (emergency.dispatch) parts.push(`${localized(language, "Numero unico", "General emergency", "Emergencias")}: ${emergency.dispatch}`);
  if (emergency.police) parts.push(`${localized(language, "Polizia", "Police", "Policía")}: ${emergency.police}`);
  if (emergency.ambulance) parts.push(`${localized(language, "Ambulanza", "Ambulance", "Ambulancia")}: ${emergency.ambulance}`);
  if (emergency.fire && emergency.fire !== emergency.ambulance) parts.push(`${localized(language, "Vigili del fuoco", "Fire brigade", "Bomberos")}: ${emergency.fire}`);
  return parts.join(" · ");
}

function formatTapWater(value: boolean | null | undefined, language: SupportedLanguage): string {
  if (value === true) return localized(language, "Acqua del rubinetto generalmente considerata potabile.", "Tap water is generally considered safe to drink.", "El agua del grifo se considera generalmente potable.");
  if (value === false) return localized(language, "Acqua del rubinetto non generalmente considerata sicura da bere: preferisci acqua confezionata e verifica le indicazioni locali.", "Tap water is not generally considered safe to drink: prefer bottled water and check local guidance.", "El agua del grifo no se considera generalmente segura para beber: utiliza agua embotellada y verifica las indicaciones locales.");
  return "";
}

async function fetchTravelRequirementsIndex(): Promise<TravelRequirementsIndex | null> {
  const cached = readCache<TravelRequirementsIndex>(REQUIREMENTS_INDEX_CACHE_KEY, REQUIREMENTS_CACHE_MS);
  if (cached) return cached;
  const index = await fetchJson<TravelRequirementsIndex>(REQUIREMENTS_INDEX_URL);
  if (index) writeCache(REQUIREMENTS_INDEX_CACHE_KEY, index);
  return index;
}

async function fetchTravelDestinationRecord(countryCode: string): Promise<{ index: TravelRequirementsIndex; record: TravelDestinationRecord } | null> {
  const code = String(countryCode || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return null;

  const index = await fetchTravelRequirementsIndex();
  if (!index) return null;
  const destination = (index.destinations || []).find((item) => String(item.iso2 || "").toUpperCase() === code);
  if (!destination?.url) return null;

  const cacheKey = `${REQUIREMENTS_DESTINATION_CACHE_PREFIX}.${code}`;
  const cached = readCache<TravelDestinationRecord>(cacheKey, REQUIREMENTS_CACHE_MS);
  if (cached) return { index, record: cached };

  const record = await fetchJson<TravelDestinationRecord>(destination.url);
  if (!record) return null;
  writeCache(cacheKey, record);
  return { index, record };
}

function formatSafetyLevel(level: string | undefined, language: SupportedLanguage): string {
  switch (String(level || "").trim()) {
    case "very-safe": return localized(language, "Destinazione generalmente molto sicura. Mantieni le normali precauzioni e verifica gli avvisi ufficiali aggiornati.", "Generally very safe destination. Keep normal precautions and check current official travel advice.", "Destino generalmente muy seguro. Mantén las precauciones habituales y consulta los avisos oficiales actualizados.");
    case "generally-safe": return localized(language, "Destinazione generalmente sicura, ma possono esserci rischi locali o stagionali. Verifica gli avvisi ufficiali prima della partenza.", "Generally safe destination, though local or seasonal risks may apply. Check official travel advice before departure.", "Destino generalmente seguro, aunque puede haber riesgos locales o estacionales. Consulta los avisos oficiales antes de viajar.");
    case "exercise-caution": return localized(language, "Sono indicate cautele specifiche per questa destinazione. Consulta gli avvisi ufficiali aggiornati prima della partenza e durante il viaggio.", "Specific caution is advised for this destination. Check current official travel advice before and during the trip.", "Se recomiendan precauciones específicas para este destino. Consulta los avisos oficiales actualizados antes y durante el viaje.");
    case "high-risk": return localized(language, "Sono presenti rischi elevati o aree sconsigliate. Consulta attentamente gli avvisi ufficiali aggiornati prima di partire.", "High risks or discouraged areas are reported. Carefully check current official travel advice before travelling.", "Existen riesgos elevados o zonas desaconsejadas. Consulta detenidamente los avisos oficiales actualizados antes de viajar.");
    default: return "";
  }
}

function formatVerifiedApps(apps: string[], kind: "taxi" | "food", language: SupportedLanguage): string {
  if (apps.length) return apps.join(", ");
  return kind === "taxi"
    ? localized(
        language,
        "Nessuna delle app globali verificate nello snapshot Mundely risulta disponibile per questo Paese. Verifica gli operatori locali.",
        "None of the global ride apps verified in the Mundely snapshot is listed for this country. Check local operators.",
        "Ninguna de las apps globales de transporte verificadas en el snapshot de Mundely figura para este país. Consulta operadores locales."
      )
    : localized(
        language,
        "Nessuna delle app globali di consegna verificate nello snapshot Mundely risulta disponibile per questo Paese. Verifica i servizi locali.",
        "None of the global delivery apps verified in the Mundely snapshot is listed for this country. Check local services.",
        "Ninguna de las apps globales de entrega verificadas en el snapshot de Mundely figura para este país. Consulta servicios locales."
      );
}

function formatLgbtqGuidance(countryCode: string, language: SupportedLanguage): string {
  const snapshot = getUsefulInfoGlobalSnapshot(countryCode);
  const reviewed = USEFUL_INFO_GLOBAL_SNAPSHOT_META.reviewedAt;
  if (snapshot.lgbtqDeathPenaltyPossible) {
    const trans = snapshot.transExpressionCriminalised
      ? localized(language, " La fonte segnala anche criminalizzazione dell'espressione di genere trans.", " The source also reports criminalisation of trans gender expression.", " La fuente también señala criminalización de la expresión de género trans.")
      : "";
    return localized(
      language,
      `Human Dignity Trust segnala criminalizzazione LGBT e la possibile applicazione della pena di morte (snapshot verificato ${reviewed}). Evita esposizione pubblica e verifica le leggi ufficiali aggiornate prima e durante il viaggio.`,
      `Human Dignity Trust reports LGBT criminalisation and possible application of the death penalty (snapshot reviewed ${reviewed}). Avoid public exposure and check current official laws before and during travel.`,
      `Human Dignity Trust señala criminalización LGBT y posible aplicación de la pena de muerte (snapshot verificado ${reviewed}). Evita la exposición pública y consulta las leyes oficiales actualizadas antes y durante el viaje.`
    ) + trans;
  }
  if (snapshot.lgbtqCriminalising) {
    const trans = snapshot.transExpressionCriminalised
      ? localized(language, " La fonte segnala anche criminalizzazione dell'espressione di genere trans.", " The source also reports criminalisation of trans gender expression.", " La fuente también señala criminalización de la expresión de género trans.")
      : "";
    return localized(
      language,
      `Human Dignity Trust segnala criminalizzazione LGBT in questa giurisdizione (snapshot verificato ${reviewed}). Usa particolare cautela e verifica le leggi ufficiali aggiornate.`,
      `Human Dignity Trust reports LGBT criminalisation in this jurisdiction (snapshot reviewed ${reviewed}). Exercise particular caution and check current official laws.`,
      `Human Dignity Trust señala criminalización LGBT en esta jurisdicción (snapshot verificado ${reviewed}). Actúa con especial cautela y consulta las leyes oficiales actualizadas.`
    ) + trans;
  }
  return localized(
    language,
    `Questa giurisdizione non compare nell'elenco di criminalizzazione LGBT di Human Dignity Trust usato dallo snapshot Mundely verificato ${reviewed}. Questo non garantisce assenza di discriminazioni o restrizioni locali: verifica il contesto aggiornato.`,
    `This jurisdiction is not listed in the Human Dignity Trust LGBT criminalisation dataset used by the Mundely snapshot reviewed ${reviewed}. This does not guarantee the absence of discrimination or local restrictions: check current local guidance.`,
    `Esta jurisdicción no figura en el listado de criminalización LGBT de Human Dignity Trust usado por el snapshot de Mundely verificado ${reviewed}. Esto no garantiza la ausencia de discriminación o restricciones locales: consulta la situación actual.`
  );
}

function formatPublicAffectionGuidance(countryCode: string, language: SupportedLanguage): string {
  const snapshot = getUsefulInfoGlobalSnapshot(countryCode);
  if (snapshot.lgbtqCriminalising) {
    return localized(
      language,
      "Per le coppie LGBTQ+ le effusioni pubbliche possono comportare rischi legali o di sicurezza; evita comportamenti espliciti e verifica le norme locali. Anche per le altre coppie è consigliata discrezione nel rispetto del contesto.",
      "For LGBTQ+ couples, public displays of affection may create legal or safety risks; avoid explicit behaviour and check local rules. Discretion is also advisable for other couples in accordance with the local context.",
      "Para las parejas LGBTQ+, las muestras públicas de afecto pueden implicar riesgos legales o de seguridad; evita comportamientos explícitos y consulta las normas locales. También se recomienda discreción a las demás parejas según el contexto."
    );
  }
  return localized(
    language,
    "Non risulta una regola globale verificata equivalente per le effusioni pubbliche: le norme sociali possono variare molto per città e contesto. Mantieni un comportamento discreto e rispettoso e verifica eventuali restrizioni locali.",
    "There is no equivalent verified global rule for public displays of affection: social norms can vary substantially by city and context. Keep behaviour discreet and respectful and check any local restrictions.",
    "No existe una regla global verificada equivalente para las muestras públicas de afecto: las normas sociales pueden variar mucho según la ciudad y el contexto. Mantén un comportamiento discreto y respetuoso y consulta posibles restricciones locales."
  );
}

// Unreviewed entity-search labels are not consular contacts. Only reviewed official profiles may supply them.
async function fetchDiplomaticMissions(nationality: string, destination: string, language: SupportedLanguage): Promise<DiplomaticMissionResult> {
  const { values } = verifiedUsefulInfo(destination, nationality, language);
  return { ...(values.embassy ? { embassy: values.embassy } : {}), ...(values.consulates ? { consulates: values.consulates } : {}) };
}

async function fetchCountryIntelligence(countryCode: string, language: SupportedLanguage): Promise<CountryIntelligence> {
  const code = String(countryCode || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return {};
  const cacheKey = `${COUNTRY_INTELLIGENCE_CACHE_PREFIX}.${code}.${language}`;
  const cached = readCache<CountryIntelligence>(cacheKey, COUNTRY_INTELLIGENCE_CACHE_MS);
  if (cached) return cached;
  const lang = language === "it" ? "it" : language === "es" ? "es" : "en";
  const query = `SELECT ?governmentLabel ?religionLabel WHERE { ?country wdt:P297 "${code}". OPTIONAL { ?country wdt:P122 ?government. } OPTIONAL { { ?country wdt:P3075 ?religion. } UNION { ?country wdt:P140 ?religion. } } SERVICE wikibase:label { bd:serviceParam wikibase:language "${lang},en". ?government rdfs:label ?governmentLabel. ?religion rdfs:label ?religionLabel. } }`;
  const payload = await fetchJson<WikidataCountryIntelligenceResponse>(`https://query.wikidata.org/sparql?format=json&query=${encodeURIComponent(query)}`);
  const bindings = payload?.results?.bindings || [];
  const governments = [...new Set(bindings.map((row) => String(row.governmentLabel?.value || "").trim()).filter(Boolean))];
  const religions = [...new Set(bindings.map((row) => String(row.religionLabel?.value || "").trim()).filter(Boolean))];
  const result: CountryIntelligence = { governmentType: governments.join(" / "), religion: religions.join(" / ") };
  if (result.governmentType || result.religion) writeCache(cacheKey, result);
  return result;
}

export async function fetchUsefulInfoTravelRequirements(
  destinationCountryCode: string,
  nationalityCode: string,
  language: SupportedLanguage
): Promise<UsefulInfoTravelRequirementsResult | null> {
  const destinationCode = String(destinationCountryCode || "").trim().toUpperCase();
  const passportCode = String(nationalityCode || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(destinationCode)) return null;

  const official = verifiedUsefulInfo(destinationCode, passportCode, language);
  if (destinationCode === "AT" || (destinationCode === "IT" && passportCode === "IT")) return { values: official.values, datasetVersion: VERIFIED_USEFUL_INFO_VERSION, updatedAt: "2026-10-19" };
  const response = await fetchTravelDestinationRecord(destinationCode);
  const index = response?.index;
  const record: TravelDestinationRecord = response?.record || {};
  const [countryIntelligence, diplomaticMissions] = await Promise.all([
    fetchCountryIntelligence(destinationCode, language),
    fetchDiplomaticMissions(passportCode, destinationCode, language)
  ]);
  const globalSnapshot = getUsefulInfoGlobalSnapshot(destinationCode);
  const visaRule = /^[A-Z]{2}$/.test(passportCode) ? record.visaPolicy?.byPassport?.[passportCode] : undefined;
  const passportValidity = record.entryRequirements?.passportValidity;
  const blankPages = record.entryRequirements?.blankPages?.count;
  const vaccinations = record.entryRequirements?.vaccinations;
  const idp = record.countryFacts?.driving?.idp;

  const values: Partial<Record<UsefulInfoFieldId, string>> = {};
  const visa = formatVisaRule(visaRule, language);
  const passport = formatPassportRequirement(passportValidity, blankPages, visaRule, language);
  const requiredVaccines = formatVaccinationRequirements(vaccinations, language);
  const internationalDrivingPermit = formatDrivingPermit(idp, language);
  const emergencyNumber = formatTravelRequirementsEmergency(record.countryFacts, language);
  const drinkingWater = formatTapWater(record.countryFacts?.tapWaterSafe, language);
  const specialPrecautions = formatSafetyLevel(record.countryFacts?.safety?.level, language);
  const taxiApps = formatVerifiedApps(globalSnapshot.taxiApps, "taxi", language);
  const foodDeliveryApps = formatVerifiedApps(globalSnapshot.foodDeliveryApps, "food", language);
  const publicAffection = formatPublicAffectionGuidance(destinationCode, language);
  const lgbtq = formatLgbtqGuidance(destinationCode, language);

  if (visa) values.visa = visa;
  if (passport) values.passport = passport;
  if (requiredVaccines) values.requiredVaccines = requiredVaccines;
  if (internationalDrivingPermit) values.internationalDrivingPermit = internationalDrivingPermit;
  if (emergencyNumber) values.emergencyNumber = emergencyNumber;
  if (drinkingWater) values.drinkingWater = drinkingWater;
  if (specialPrecautions) values.specialPrecautions = specialPrecautions;
  if (taxiApps) values.taxiApps = taxiApps;
  if (foodDeliveryApps) values.foodDeliveryApps = foodDeliveryApps;
  if (publicAffection) values.publicAffection = publicAffection;
  if (lgbtq) values.lgbtq = lgbtq;
  if (countryIntelligence.governmentType) values.governmentType = countryIntelligence.governmentType;
  if (countryIntelligence.religion) values.religion = countryIntelligence.religion;
  if (diplomaticMissions.embassy) values.embassy = diplomaticMissions.embassy;
  if (diplomaticMissions.consulates) values.consulates = diplomaticMissions.consulates;

  const updatedAt = [record.meta?.lastUpdated, index?.dateModified, USEFUL_INFO_GLOBAL_SNAPSHOT_META.reviewedAt]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .sort()
    .at(-1) || new Date().toISOString().slice(0, 10);

  return {
    values,
    datasetVersion: `${String(index?.version || "unavailable")}|global:${USEFUL_INFO_GLOBAL_SNAPSHOT_META.version}`,
    updatedAt
  };
}
